import { useEffect } from "react";
import { focusManager, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ToastProvider } from "@signet/ui";
import { ApiError, desktop, SIGNED_OUT_EVENT, WebModulesProvider } from "@signet/web-kit";
import { webModules } from "../../../modules/registry.web.ts";
import { router } from "./router.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 or 403 will not fix itself on a retry.
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
      refetchOnWindowFocus: true,
      staleTime: 15_000,
    },
  },
});

export function App() {
  useEffect(() => {
    const signedOut = () => {
      queryClient.clear();
      // The quick bar stays itself and offers sign-in in the main window.
      if (location.pathname === "/quick") return;
      if (!location.pathname.startsWith("/login")) router.navigate({ to: "/login", search: { next: location.pathname } as never });
    };
    window.addEventListener(SIGNED_OUT_EVENT, signedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, signedOut);
  }, []);

  // In the desktop app, rest while the window is not in use. Queries stop
  // polling (they refetch what went stale the moment it is back in front) and
  // animations pause, so a Signet window behind other apps costs next to
  // nothing. The shell unloads the page altogether once it has been hidden a
  // while (apps/desktop/main.mjs).
  useEffect(
    () =>
      desktop?.onActivity?.((active) => {
        focusManager.setFocused(active);
        document.documentElement.classList.toggle("signet-resting", !active);
      }),
    [],
  );

  // The quick bar sends the main window where the person chose. Listened for
  // here rather than in the shell, so it also works from the sign-in page.
  useEffect(() => desktop?.onNavigate?.((to) => void router.navigate({ href: to })), []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <WebModulesProvider value={webModules}>
          <RouterProvider router={router} context={{ queryClient }} />
        </WebModulesProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
